import { ChargeTypeId, ChargeTypeKey } from '@/utils/enum'
import {
  validateForEstimate,
  isValidationSuccessful,
  getValidationErrors,
  ValidateForEstimateRequest
} from '@/services/validations'
import { getQuoteTripEstimations } from '@/services/quotes'
import { AxiosResponse } from 'axios'

/**
 * Checks if a trip has a valid base fare charge (base_fare type with positive fixedAmount).
 * Used to guard against calling price-line-items API without required base fare.
 */
export function hasValidBaseFare(trip: any): boolean {
  const baseFareCharge = trip.charges?.find(
    (charge: any) =>
      charge.chargeType?.key === ChargeTypeKey.BaseFare || charge.chargeType?.id === ChargeTypeId.BaseFare,
  )
  return baseFareCharge?.fixedAmount > 0
}

// TODO: This is a temporary fix to be replaced with a validation API
export function validateQuoteCharges(quote: any): boolean {
  // Check if any trip has charges with null chargeTypeId or chargeType
  const hasInvalidCharge = quote.trips?.some((trip) => {
    // Ensure charges array exists and is not null
    if (!trip.charges || !Array.isArray(trip.charges)) {
      return false
    }

    return trip.charges.some((charge) => {
      // Ensure charge itself is not null
      if (charge == null) {
        return true
      }

      // Check chargeTypeId is non-null
      if (charge.chargeTypeId == null) {
        return true
      }

      // Check chargeType object exists and has a valid id property
      if (charge.chargeType == null || charge.chargeType.id == null) {
        return true
      }

      return false
    })
  })

  return !hasInvalidCharge
}

// TODO: This is a temporary fix to be replaced with a validation API
export function validateQuoteStops(quote: any): boolean {
  // Check if any trip is missing required dates on first/last stops
  const hasInvalidStop = quote.trips?.some((trip) => {
    // Ensure stops array exists and is not null
    if (!trip.stops || !Array.isArray(trip.stops)) {
      return true
    }

    // Need at least 2 stops
    if (trip.stops.length < 2) {
      return true
    }

    const firstStop = trip.stops[0]
    const lastStop = trip.stops[trip.stops.length - 1]

    // First stop must have a pickupDate
    if (!firstStop?.pickupDate) {
      return true
    }

    // Last stop must have a dropoffDate
    if (!lastStop?.dropoffDate) {
      return true
    }

    return false
  })

  return !hasInvalidStop
}

export interface TripEstimationsAddress {
  name?: string
  country?: string
  lat?: number
  lng?: number
  timeZone?: string
}

export interface TripEstimationsStop {
  orderIndex: number
  active?: boolean
  address?: TripEstimationsAddress
  pickupDatetime?: string
  dropoffDatetime?: string
}

export interface TripEstimationsVehicle {
  vehicleTypeKey?: string
  vehicleTypeId?: number
}

export interface TripEstimationsTrip {
  tripId?: number
  orderIndex?: number
  stops: TripEstimationsStop[]
  vehicles?: TripEstimationsVehicle[]
}

export interface TripEstimationsPayload {
  companyId?: number
  trips: TripEstimationsTrip[]
}

/**
 * Transforms trip estimations payload to validation API format for trip estimations validation.
 * Trip estimations uses { trips: [...] } but validation API expects { quote: { trips: [...] } }
 */
function toTripEstimationsValidationPayload(tripEstimationsPayload: TripEstimationsPayload): ValidateForEstimateRequest {
  return {
    quote: {
      companyId: tripEstimationsPayload.companyId,
      trips: tripEstimationsPayload.trips.map((trip: TripEstimationsTrip, index: number) => ({
        tripId: trip.tripId,
        orderIndex: trip.orderIndex ?? index,
        stops: trip.stops.map((stop: TripEstimationsStop) => ({
          orderIndex: stop.orderIndex,
          active: stop.active,
          address: stop.address ? {
            lat: stop.address.lat,
            lng: stop.address.lng,
            timezone: stop.address.timeZone,
            country: stop.address.country
          } : undefined,
          pickupDatetime: stop.pickupDatetime,
          dropoffDatetime: stop.dropoffDatetime
        })),
        vehicles: trip.vehicles?.map((v: TripEstimationsVehicle) => ({
          vehicleTypeId: v.vehicleTypeId
        }))
      }))
    }
  }
}

export interface TripEstimationsResult {
  valid: boolean
  response?: AxiosResponse<any>
  errorMessage?: string
}

/**
 * Validates the quote data and then calls the trip estimations API.
 * This ensures quote validation is always performed before requesting estimations.
 * @param payload - The same payload that would be sent to /tripEstimations
 * @returns Result object indicating success/failure and the response if successful
 */
export async function validateQuoteAndGetTripEstimations(payload: TripEstimationsPayload): Promise<TripEstimationsResult> {
  const validationPayload = toTripEstimationsValidationPayload(payload)
  const validationResponse = await validateForEstimate(validationPayload)

  if (!isValidationSuccessful(validationResponse.data)) {
    const errors = getValidationErrors(validationResponse.data)
    const errorMessage = errors.join(', ') || 'Validation failed'
    return { valid: false, errorMessage }
  }

  const response = await getQuoteTripEstimations(payload)
  return { valid: true, response }
}
